import { useToast } from '@televet/kibble-ui';
import { useCallback, useMemo } from 'react';
import {
  useGetClinicIsServiceRemindersActiveQuery,
  useGetReminderSettingsClinicServicesFormTemplateIdQuery,
  useToggleClinicIsServiceRemindersActiveMutation,
} from 'shared/types/graphql';

interface useServiceRemindersReturn {
  clinicHasServiceReminders: boolean;
  serviceReminderDataLoading: boolean;
  toggleClinicHasServiceReminders: () => void;
}

export const useServiceReminders = (currentClinicId?: string): useServiceRemindersReturn => {
  const toast = useToast();
  const { data: serviceReminderData, loading: serviceReminderDataLoading } = useGetClinicIsServiceRemindersActiveQuery({
    variables: {
      clinicId: currentClinicId || '',
    },
    skip: !currentClinicId,
  });

  const { data: serviceReminderFormTemplateData } = useGetReminderSettingsClinicServicesFormTemplateIdQuery({
    variables: { clinicId: currentClinicId || '' },
  });

  const clinicHasServiceReminders = useMemo(
    () => !!serviceReminderData?.findUniqueClinic?.isServiceRemindersActive,
    [serviceReminderData?.findUniqueClinic?.isServiceRemindersActive],
  );

  const [toggleClinicHasServiceReminders] = useToggleClinicIsServiceRemindersActiveMutation({
    variables: {
      clinicId: currentClinicId || '',
      isServiceRemindersActive: !clinicHasServiceReminders,
    },
  });

  const checkToggleServiceRemindersAbility = useCallback(() => {
    // If enabling service reminders, ensure that the service reminder form template exists
    const serviceReminderForm = serviceReminderFormTemplateData?.findFirstFormTemplate;
    if (!clinicHasServiceReminders && (!serviceReminderForm?.isActive || serviceReminderForm?.isDeleted)) {
      toast({
        title: 'Cannot Enable Service Reminders',
        description: 'Appointment Request Form is missing. Please contact support to have the form created.',
        status: 'error',
      });
    } else {
      toggleClinicHasServiceReminders();
    }
  }, [serviceReminderFormTemplateData, clinicHasServiceReminders, toggleClinicHasServiceReminders, toast]);

  return {
    serviceReminderDataLoading,
    clinicHasServiceReminders,
    toggleClinicHasServiceReminders: checkToggleServiceRemindersAbility,
  };
};
